import { Button, Paper, Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import ClearIcon from '@material-ui/icons/ClearSharp'
import { LineString } from '@turf/helpers'
import length from '@turf/length'
import classNames from 'classnames'
import { upperFirst } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DistanceUnit } from 'src/api/types/units'
import { useDraw } from 'src/commons/map/DrawLayer'
import { useAgencySettingsDistanceUnit } from 'src/layout/AgencySettings'

export const get_turf_unit = (agencyUnit: DistanceUnit) => {
  switch (agencyUnit) {
    case DistanceUnit.Kilometer:
      return 'kilometers'
    case DistanceUnit.Meter:
      return 'meters'
    case DistanceUnit.Mile:
      return 'miles'
    default:
      return 'kilometers'
  }
}

interface IMeasurementPanel {
  onCancel?: () => void
}

function MeasurementPanel(props: IMeasurementPanel) {
  const { t } = useTranslation()
  const classes = useStyles()
  const { draw, actions } = useDraw()

  const { value: distanceUnit, symbol: distanceUnitSymbol } = useAgencySettingsDistanceUnit()

  const [distance, setDistance] = useState<number | undefined>(undefined)

  const title = upperFirst(
    distance
      ? `${t('titles.distance')} : ${distance.toFixed(2)} ${distanceUnitSymbol}`
      : t('please draw the path to measure and double-click to end')
  )

  useEffect(() => {
    if (draw && draw.features.length) {
      const ls: LineString = draw.features[0].geometry as LineString
      if (ls.coordinates.length > 1) {
        setDistance(length(draw, { units: get_turf_unit(distanceUnit) }))
        return
      }
    }
    setDistance(undefined)
  }, [draw?.features])

  return (
    <Paper elevation={2} className={classes.root}>
      <div
        className={classes.closeBtn}
        onClick={() => {
          actions.deleteAllFeatures()
          if (props.onCancel) {
            props.onCancel()
          }
        }}
      >
        <ClearIcon />
      </div>
      <div className={classes.stepContent}>
        <Typography variant="h5">{title}</Typography>
      </div>
      <div className={classes.actionsContainer}>
        <Button
          id="delete"
          variant="outlined"
          color="primary"
          disabled={!draw}
          onClick={actions.deleteAllFeatures}
          className={classNames(classes.button, classes.deletionBtn)}
        >
          {t('button.reset')}
        </Button>
      </div>
    </Paper>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    zIndex: theme.zIndex.drawer,
    margin: '5px',
    maxWidth: '500px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#FFF',
    padding: theme.spacing(2),
  },
  actionsContainer: {
    display: 'flex',
    justifyContent: 'space-around',
  },
  closeBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    right: theme.spacing(),
    top: theme.spacing(),
    color: theme.palette.secondary.dark,
    cursor: 'pointer',
  },
  stepContent: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    padding: theme.spacing(4),
  },
  button: {
    marginLeft: theme.spacing(),
    marginRight: theme.spacing(),
  },
  deletionBtn: {
    border: `solid 1px ${theme.palette.error.main}`,
    color: theme.palette.error.main,
  },
}))

export default MeasurementPanel
